import { type FunctionComponent } from 'preact'
import { html } from 'htm/preact'
import type { AppState } from '../state.js'
// import { APP_SIGNUP_URL, EM_DASH } from '../constants.js'
import { APP_SIGNUP_URL } from '../constants.js'
// import { PRICING_PLANS } from '../data/pricing.js'
import { FAQ_ITEMS } from '../data/faq.js'
import '@substrate-system/details-summary/css'
import { DetailsSummary } from '@substrate-system/details-summary'
import './home.css'
import { NotifyForm } from '../components/notify-form.js'

export const HomeRoute:FunctionComponent<{
    state:AppState
}> = function HomeRoute () {
    return html`<div class="route home">
        <section id="hero" class="landing-hero">
            <div class="hero-body">
                <p class="hero-eyebrow">EPA Section 608 compliance</p>
                <h1 class="hero-headline">
                    A refrigerant log that works<br />
                    wherever you do, including offline.
                </h1>
                <p class="hero-subhead">
                    <code>hvaclog</code> captures every relevant action
                    on the job site, then keeps your
                    EPA 608 records at the office.
                    No clipboard, no end-of-day transcription.
                </p>
                <div class="hero-cta-group">
                    <a
                        class="btn btn-primary"
                        href=${APP_SIGNUP_URL}
                    >Start free</a>
                    <a
                        class="btn btn-secondary"
                        href="/#how-it-works"
                    >See how it works</a>
                </div>
                <${NotifyForm} />
            </div>
        </section>

        <section id="problem" class="landing-problem">
            <div class="problem-inner">
                <h2 class="problem-heading">
                    The old way wastes time, and puts you at risk.
                </h2>
                <p class="problem-intro">
                    Every job requires records.
                    Most shops handle it with a notebook,
                    a spreadsheet at the office,
                    and a quarterly scramble to reconcile them for an audit.
                </p>

                <div class="problem-comparison">
                    <div class="problem-col problem-col-before">
                        <h3 class="problem-col-heading">Without hvaclog</h3>
                        <ol class="problem-steps">
                            <li>Jot refrigerant amounts in a notebook
                                or notes app on-site</li>
                            <li>Return to the office and manually enter
                                data into a spreadsheet or log binder</li>
                            <li>Compile records before each audit and
                                hope nothing was missed or misread</li>
                        </ol>
                        <p class="problem-risk">
                            Under EPA Section 608, systems charged with
                            50+ lb of refrigerant must be repaired when the
                            annual leak rate exceeds the applicable
                            threshold. Spotty records make it hard to know
                            when that threshold is crossed.
                        </p>
                    </div>

                    <div class="problem-col problem-col-after">
                        <h3 class="problem-col-heading">With hvaclog</h3>
                        <ol class="problem-steps">
                            <li>Tap to log the transaction on-site,
                                even offline</li>
                            <li>Add a photo or note if needed.
                                The record is timestamped and saved</li>
                            <li>Sync when you are back online; audit-ready
                                reports generate in one tap</li>
                        </ol>
                        <p class="problem-reassurance">
                            Every entry is stored with what matters:
                            date, system, refrigerant type,
                            quantity, and technician. No transcription step.
                            No lost notebooks.
                        </p>
                    </div>
                </div>
            </div>
        </section>

        <section id="how-it-works" class="landing-how">
            <div class="how-inner">
                <h2 class="how-heading">How it works</h2>
                <ol class="how-steps">
                    <li class="how-step">
                        <span class="how-ordinal" aria-hidden="true">1</span>
                        <div class="how-content">
                            <h3 class="how-step-heading">
                                Create logs on-site, even with no signal.
                            </h3>
                            <p class="how-step-body">
                                Open the app and tap to record each refrigerant
                                transaction the moment it happens. The entry
                                saves locally first, which means no Wi-Fi,
                                no cell service, no problem. Basements,
                                rooftops, and remote sites all work the same way.
                            </p>
                        </div>
                    </li>
                    <li class="how-step">
                        <span class="how-ordinal" aria-hidden="true">2</span>
                        <div class="how-content">
                            <h3 class="how-step-heading">
                                Sync at end of day
                            </h3>
                            <p class="how-step-body">
                                When your device reconnects, hvaclog syncs
                                automatically. Every job from
                                the day uploads without any manual step.
                            </p>
                        </div>
                    </li>
                    <li class="how-step">
                        <span class="how-ordinal" aria-hidden="true">3</span>
                        <div class="how-content">
                            <h3 class="how-step-heading">
                                Export for a quarterly audit in one tap
                            </h3>
                            <p class="how-step-body">
                                Generate a complete
                                PDF log for any date range. The
                                report is formatted the way regulators expect
                                and built from the records you already entered.
                                No spreadsheet reconciliation, no scramble.
                            </p>
                        </div>
                    </li>
                </ol>
            </div>
        </section>

        <section id="faq" class="landing-faq">
            <div class="faq-inner">
                <h2 class="faq-heading">Frequently asked questions</h2>
                <div class="faq-list">
                    ${FAQ_ITEMS.map(item => html`
                        <${DetailsSummary.TAG}>
                            <details class="faq-item">
                                <summary class="faq-question">
                                    ${item.question}
                                </summary>
                                <div class="details-content faq-answer">
                                    <p>
                                        ${item.answer}
                                    </p>
                                </div>
                            </details>
                        </$>
                    `)}
                </div>
            </div>
        </section>
    </div>`
}

// <section id="pricing" class="landing-pricing">
//     <div class="pricing-inner">
//         <h2 class="pricing-heading">Simple, transparent pricing</h2>
//         <h2 class="pricing-heading">Simple, transparent pricing</h2>
//         <p class="pricing-trial-banner">
//             Starts with a 14-day free trial.
//         </p>
//         <p class="pricing-lead">
//             One flat price for your whole shop. No per-seat
//             fees, no hidden tiers. Add technicians without changing
//             the bill.
//         </p>
//         <div class="pricing-plans">
//             ${PRICING_PLANS.map(plan => html`
//                 <div
//                     class=${'pricing-plan' +
//                         (plan.recommended ?
//                             ' pricing-plan-recommended' :
//                             '')}
//                 >
//                     ${plan.badge && html`
//                         <span class="pricing-badge">${plan.badge}</span>
//                     `}
//                     <h3 class="pricing-plan-name">${plan.name}</h3>
//                     <p class="pricing-plan-price">
//                         <span class="pricing-amount">
//                             $${plan.price}
//                         </span>
//                         <span class="pricing-period">
//                             ${plan.period === 'month' ?
//                                 '/ month' :
//                                 'one-time'}
//                         </span>
//                     </p>
//                     <p class="pricing-plan-desc">${plan.description}</p>
//                     <ul class="pricing-features">
//                         ${plan.features.map(f => html`
//                             <li class="pricing-feature">${f}</li>
//                         `)}
//                     </ul>
//                     <a
//                         class=${'btn' +
//                             (plan.recommended ?
//                                 ' btn-primary' :
//                                 ' btn-secondary')}
//                         href=${APP_SIGNUP_URL}
//                     >Start free</a>
//                 </div>
//             `)}
//         </div>
//         <p class="pricing-note">
//             Your first 14 days are free. We ask for a card up front so
//             there's no interruption ${EM_DASH} if you don't cancel,
//             your trial rolls into the Monthly plan at $149/mo when
//             it ends. Switch to the lifetime deal or cancel any time
//             before then and you won't be charged. The lifetime
//             founding deal is available while supplies last.
//         </p>
//     </div>
// </section>
